import Swiper, { Autoplay, Navigation } from 'swiper';
import { swiperObserver } from '../utils/swiper.utils.js';

const AUTOPLAY_DELAY = 5000;
const INITIAL_SLIDE_INDEX = 1;
const DESKTOP_SIDE_SCALE = 0.72;
const MOBILE_SIDE_SCALE = 0.82;
const SIDE_OPACITY = 0.55;
const HIDDEN_PROGRESS = 1.65;
const CLONE_ATTR = 'data-staffing-clone';


const getSideScale = () => {
  const w = window.innerWidth;
  if (w < 440) return 0.84;
  if (w < 768) return 0.84;
  if (w < 1024) return 0.78;
  return DESKTOP_SIDE_SCALE;
};

const getSlideOpacity = (progress) => {
  const absProgress = Math.abs(progress);

  if (absProgress <= 1) {
    return 1 - ((1 - SIDE_OPACITY) * absProgress);
  }

  if (absProgress < HIDDEN_PROGRESS) {
    return SIDE_OPACITY * (1 - ((absProgress - 1) / (HIDDEN_PROGRESS - 1)));
  }

  return 0;
};

const updateSlideTransition = (swiper, duration = swiper.params.speed) => {
  const opacityDuration = Math.min(duration, 520);

  Array.from(swiper.slides).forEach((slide) => {
    slide.style.transitionDuration = `${duration}ms, ${opacityDuration}ms`;
  });
};

const setWillChange = (swiper, active) => {
  Array.from(swiper.slides).forEach((slide) => {
    slide.style.willChange = active ? 'transform, opacity' : 'auto';
  });
};

const restoreSlideTransition = (swiper) => {
  window.requestAnimationFrame(() => {
    updateSlideTransition(swiper);
  });
};

const updateSlidesView = (swiper) => {
  const sideScale = getSideScale();

  Array.from(swiper.slides).forEach((slide) => {
    const progress = Number.isFinite(slide.progress) ? slide.progress : 0;
    const absProgress = Math.min(Math.abs(progress), HIDDEN_PROGRESS);
    const opacity = getSlideOpacity(progress);
    const scale = absProgress <= 1
        ? 1 - ((1 - sideScale) * absProgress)
        : sideScale;

    slide.style.opacity = opacity.toFixed(3);
    slide.style.transform = `scale(${scale.toFixed(4)})`;
    slide.style.zIndex = `${Math.round((HIDDEN_PROGRESS - absProgress) * 100)}`;
    slide.style.pointerEvents = absProgress < 0.35 ? 'auto' : 'none';
  });
};

const prepareSlides = (swiperEl) => {
  const wrapper = swiperEl.querySelector('.swiper-wrapper');

  if (!wrapper) {
    return 0;
  }

  const originalSlides = Array.from(wrapper.querySelectorAll(`.swiper-slide:not([${CLONE_ATTR}])`));
  const slidesCount = originalSlides.length;

  if (slidesCount > 1 && !wrapper.querySelector(`[${CLONE_ATTR}]`)) {
    const beforeFragment = document.createDocumentFragment();
    const afterFragment = document.createDocumentFragment();

    originalSlides.forEach((slide, index) => {
      slide.dataset.staffingBaseIndex = `${index}`;

      const beforeClone = slide.cloneNode(true);
      const afterClone = slide.cloneNode(true);

      beforeClone.setAttribute(CLONE_ATTR, 'true');
      afterClone.setAttribute(CLONE_ATTR, 'true');
      beforeClone.dataset.staffingBaseIndex = `${index}`;
      afterClone.dataset.staffingBaseIndex = `${index}`;

      beforeFragment.appendChild(beforeClone);
      afterFragment.appendChild(afterClone);
    });

    wrapper.insertBefore(beforeFragment, wrapper.firstChild);
    wrapper.appendChild(afterFragment);
  }

  return slidesCount;
};

const normalizeSlidesPosition = (swiper, slidesCount) => {
  if (slidesCount <= 1) {
    return;
  }

  if (swiper.activeIndex >= slidesCount * 2) {
    swiper.slideTo(swiper.activeIndex - slidesCount, 0, false);
    updateSlidesView(swiper);
    restoreSlideTransition(swiper);
    return;
  }

  if (swiper.activeIndex < slidesCount) {
    swiper.slideTo(swiper.activeIndex + slidesCount, 0, false);
    updateSlidesView(swiper);
    restoreSlideTransition(swiper);
  }
};

const initStaffingSwiper = (scope = document) => {
  const wrappers = scope.querySelectorAll('.staffing-swiper-wr');

  wrappers.forEach((el) => {
    const swiperEl = el.querySelector('.swiper');

    if (!swiperEl || swiperEl.swiper) {
      return;
    }

    const slidesCount = prepareSlides(swiperEl);

    if (!slidesCount) {
      return;
    }

    const nextEl = el.querySelector('.slider-btn.next');
    const prevEl = el.querySelector('.slider-btn.prev');
    const swiper = new Swiper(swiperEl, {
      modules: [Autoplay, Navigation],
      observer: true,
      observeParents: true,
      watchSlidesProgress: true,
      centeredSlides: true,
      slidesPerView: 'auto',
      spaceBetween: -160,
      initialSlide: slidesCount > 1
          ? slidesCount + (slidesCount > INITIAL_SLIDE_INDEX ? INITIAL_SLIDE_INDEX : 0)
          : 0,
      loop: false,
      speed: 850,
      threshold: 10,
      grabCursor: true,
      navigation: nextEl && prevEl ? {
        nextEl,
        prevEl,
      } : undefined,
      breakpoints: {
        440: {
          spaceBetween: -160,
        },
        768: {
          spaceBetween: -280,
        },
        1024: {
          spaceBetween: -340,
        },
        1280: {
          spaceBetween: -410,
        },
      },
      autoplay: slidesCount > 1 ? {
        delay: AUTOPLAY_DELAY,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      } : false,
      on: {
        init(swiperInstance) {
          swiperInstance.el.closest('.staffing-swiper-wr')?.classList.add('is-staffing-ready');
          updateSlideTransition(swiperInstance, 0);
          updateSlidesView(swiperInstance);
          restoreSlideTransition(swiperInstance);
        },
        destroy(swiperInstance) {
          swiperInstance.el.closest('.staffing-swiper-wr')?.classList.remove('is-staffing-ready');
        },
        setTranslate(swiperInstance) {
          updateSlidesView(swiperInstance);
        },
        setTransition(swiperInstance, duration) {
          if (duration > 0) {
            setWillChange(swiperInstance, true);
          }
          updateSlideTransition(swiperInstance, duration);
        },
        resize(swiperInstance) {
          updateSlidesView(swiperInstance);
        },
        transitionEnd(swiperInstance) {
          normalizeSlidesPosition(swiperInstance, slidesCount);
          updateSlidesView(swiperInstance);
          restoreSlideTransition(swiperInstance);
          requestAnimationFrame(() => {
            setWillChange(swiperInstance, false);
          });
        },
      },
    });

    swiperObserver(swiper);
  });
};

initStaffingSwiper();
window.initStaffingSwiper = initStaffingSwiper;
